import React from 'react';
import moment from 'moment';
import { BookOfferResponseModel } from '../../../../../shared/ui-models/book.ui-model';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { FeeType } from '../../../../../shared/enums/amount-type.enum';
import { PRIMARY_BUTTON, formatMoney } from '../../reservation-create.ui';

type PaymentLine = BookOfferResponseModel['oneTimePayment'][number];

export interface SummaryRailProps {
  data: {
    unitType?: UnitTypeUiModel;
    roomName: string;
    dates?: Date[];
    guestName: string;
    bookOffer?: BookOfferResponseModel;
    isOfferLoading: boolean;
    canCreate: boolean;
    isCreating: boolean;
  };
  eventHandlers: {
    onCreate: () => void;
  };
}

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start justify-between gap-[16px] text-[14px]">
    <span className="pt-[2px] text-[12px] font-gilroy-medium text-neutral-300">{label}</span>
    <span className={`text-right ${value ? 'font-gilroy-medium' : 'text-neutral-100'}`}>
      {value || '—'}
    </span>
  </div>
);

const lineLabel = (line: PaymentLine) => {
  const name = line.feeName === 'Unit Price' ? 'Rent' : line.feeName;
  if (line.feeType === FeeType.PERCENTAGE) return `${name} (${line.percentValue}%)`;
  if (line.feeName === 'Unit Price' && line.duration) {
    const unit = line.mode === 'monthly' ? 'month' : 'night';
    return `${name} · ${line.duration} ${unit}${line.duration === 1 ? '' : 's'}`;
  }
  if (line.startAtMonth) return `${name} (from month ${line.startAtMonth})`;
  return name;
};

const PaymentLines = ({
  title,
  lines,
  currency
}: {
  title?: string;
  lines: PaymentLine[];
  currency?: string;
}) => (
  <div className="flex flex-col gap-[10px]">
    {title && <span className="text-[12px] font-gilroy-medium text-neutral-300">{title}</span>}
    {lines.map((line, index) => {
      const lineCurrency = line.currency || currency;
      const isDiscounted =
        line.feeType !== FeeType.PERCENTAGE &&
        !!line.originalPrice &&
        line.originalPrice !== line.feeValue;
      return (
        <div
          key={`${line.feeName}-${index}`}
          className="flex items-baseline justify-between gap-[12px] text-[14px]"
        >
          <span className={line.feeName === 'Unit Price' ? '' : 'text-neutral-300'}>
            {lineLabel(line)}
          </span>
          <span className="inline-flex items-baseline gap-[6px] whitespace-nowrap">
            {isDiscounted && (
              <span className="text-[12px] text-neutral-300 line-through">
                {formatMoney(line.originalPrice, lineCurrency)}
              </span>
            )}
            {formatMoney(Number(line.feeValue), lineCurrency)}
          </span>
        </div>
      );
    })}
    <div className="flex items-center justify-between pt-[10px] border-t border-gray-200">
      <span className="text-[15px] font-gilroy-medium">Total</span>
      <span className="text-[18px] font-gilroy-bold">
        {formatMoney(
          lines.reduce((sum, line) => sum + Number(line.feeValue), 0),
          lines[0]?.currency || currency
        )}
      </span>
    </div>
  </div>
);

export const SummaryRail: React.FC<SummaryRailProps> = ({
  data: { unitType, roomName, dates, guestName, bookOffer, isOfferLoading, canCreate, isCreating },
  eventHandlers: { onCreate }
}) => {
  const hasDates = !!dates && dates.length === 2;
  const hasRecurring = !!bookOffer?.recurringPayment?.length;

  return (
    <div className="xl:sticky xl:top-[88px] flex flex-col gap-[20px] rounded-[16px] border border-gray-200 p-[24px]">
      <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
        Summary
      </h2>
      <div className="flex flex-col gap-[12px]">
        <SummaryRow label="Unit" value={[unitType?.name, roomName].filter(Boolean).join(' · ')} />
        <SummaryRow
          label="Dates"
          value={
            hasDates
              ? `${moment(dates![0]).format('D MMM')} – ${moment(dates![1]).format('D MMM, YYYY')}`
              : ''
          }
        />
        <SummaryRow label="Guest" value={guestName} />
      </div>
      <div className="flex flex-col gap-[20px] border-t border-gray-200 pt-[20px]">
        {isOfferLoading ? (
          <span className="text-[14px] text-neutral-300">Calculating price…</span>
        ) : bookOffer ? (
          <>
            {!!bookOffer.oneTimePayment?.length && (
              <PaymentLines
                title={hasRecurring ? 'One-time payment' : undefined}
                lines={bookOffer.oneTimePayment}
                currency={unitType?.currency}
              />
            )}
            {hasRecurring && (
              <PaymentLines
                title="Monthly payment"
                lines={bookOffer.recurringPayment}
                currency={unitType?.currency}
              />
            )}
          </>
        ) : (
          <span className="text-[14px] text-neutral-300">Pick a unit and dates</span>
        )}
      </div>
      <button
        type="button"
        className={`${PRIMARY_BUTTON} w-full`}
        disabled={!canCreate || isCreating}
        onClick={onCreate}
      >
        {isCreating ? 'Creating…' : 'Create reservation'}
      </button>
    </div>
  );
};
